import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  PropsWithChildren,
  ReactElement
} from 'react'
import {
  AuthUser,
  AuthTokens,
  getGoogleAuthUrl,
  getMicrosoftAuthUrl,
  getCurrentUser,
  refreshAccessToken,
  signOutApi
} from './api'

interface AuthContextType {
  user: AuthUser | null
  loading: boolean
  signInWithGoogle: () => void
  signInWithMicrosoft: () => void
  signOut: () => Promise<void>
  setAuthFromCallback: (user: AuthUser, tokens: AuthTokens) => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: false,
  signInWithGoogle: () => undefined,
  signInWithMicrosoft: () => undefined,
  signOut: () => Promise.resolve(),
  setAuthFromCallback: () => undefined,
  isAuthenticated: false
})

const TOKEN_KEY = 'ameritax_tokens'
const USER_KEY = 'ameritax_user'

const saveTokens = (tokens: AuthTokens): void => {
  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens))
  } catch {
    // localStorage might be full or unavailable
  }
}

const getStoredTokens = (): AuthTokens | null => {
  try {
    const stored = localStorage.getItem(TOKEN_KEY)
    return stored ? (JSON.parse(stored) as AuthTokens) : null
  } catch {
    return null
  }
}

const saveUser = (user: AuthUser): void => {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  } catch {
    // localStorage might be full or unavailable
  }
}

const getStoredUser = (): AuthUser | null => {
  try {
    const stored = localStorage.getItem(USER_KEY)
    return stored ? (JSON.parse(stored) as AuthUser) : null
  } catch {
    return null
  }
}

const clearStorage = (): void => {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

export const AuthProvider = ({
  children
}: PropsWithChildren<Record<string, unknown>>): ReactElement => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const restoreSession = async () => {
      const tokens = getStoredTokens()
      const storedUser = getStoredUser()

      if (!tokens) {
        setLoading(false)
        return
      }

      if (tokens.expiresAt > Date.now()) {
        try {
          const freshUser = await getCurrentUser(tokens.accessToken)
          setUser(freshUser)
          saveUser(freshUser)
        } catch {
          if (storedUser) {
            setUser(storedUser)
          }
        }
      } else if (tokens.refreshToken) {
        try {
          const newTokens = await refreshAccessToken(tokens.refreshToken)
          saveTokens(newTokens)
          const freshUser = await getCurrentUser(newTokens.accessToken)
          setUser(freshUser)
          saveUser(freshUser)
        } catch {
          clearStorage()
        }
      } else {
        clearStorage()
      }

      setLoading(false)
    }

    void restoreSession()
  }, [])

  const signInWithGoogle = useCallback(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID
    if (!clientId) return
    window.location.href = getGoogleAuthUrl()
  }, [])

  const signInWithMicrosoft = useCallback(() => {
    const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID
    if (!clientId) return
    window.location.href = getMicrosoftAuthUrl()
  }, [])

  const signOut = useCallback(async () => {
    const tokens = getStoredTokens()
    if (tokens) {
      try {
        await signOutApi(tokens.accessToken)
      } catch {
        // Server unreachable - sign out locally
      }
    }
    clearStorage()
    setUser(null)
  }, [])

  const setAuthFromCallback = useCallback(
    (newUser: AuthUser, tokens: AuthTokens) => {
      setUser(newUser)
      saveTokens(tokens)
      saveUser(newUser)
      setLoading(false)
    },
    []
  )

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signInWithGoogle,
        signInWithMicrosoft,
        signOut,
        setAuthFromCallback,
        isAuthenticated: user !== null
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => useContext(AuthContext)
